import React, { useCallback, useLayoutEffect, useRef, useState } from 'react';
import {
  BiCopy,
  BiInfoCircle,
  BiLink,
  BiRefresh,
  BiTrash,
} from 'react-icons/bi';
import { MdDragIndicator } from 'react-icons/md';

import { Tooltip } from 'antd';
import {
  CHANGE_SEED_TARGET_MENU_LABEL,
  DUPLICATE_TASK_MENU_LABEL,
  STEP_REORDER_CONTROL_ATTR,
  UPDATE_DEFAULTS_MENU_LABEL,
} from 'constants/uiConstants';
import { pxToRem } from 'utils/cssUnits';
import MergeClassNames from 'utils/MergeClassNames';

/**
 * Reveal contract shared by the row's pointer-driven controls: hidden and
 * inert until the row is hovered or holds focus, so that on a device without
 * hover a control which never becomes visible cannot swallow taps.
 */
const ROW_CONTROL_REVEAL_CLASSES = [
  'pointer-events-none opacity-0',
  'group-hover:pointer-events-auto group-hover:opacity-100',
  'group-focus-within:pointer-events-auto group-focus-within:opacity-100',
];

/** Forced visible while a control is the active one for its row. */
const ROW_CONTROL_ACTIVE_CLASSES = 'pointer-events-auto opacity-100';

interface TaskRowContextMenuProps {
  x: number;
  y: number;
  /**
   * Treat `x` as the menu's right edge rather than its left. Used by the row's
   * three-dot button, which sits at the right of the row — hanging the menu
   * leftward from it keeps the whole menu over the grid.
   */
  alignRight: boolean;
  showViewDetails: boolean;
  /** Step rows only, and only where the task has more than one visible step. */
  showReorder: boolean;
  /** Seed Treatment (SeedDress) step rows only. */
  showChangeSeedTarget?: boolean;
  /** Input step rows with a catalogue product only (OP-2499). */
  showUpdateDefaults?: boolean;
  /**
   * When set, Delete is shown but inert and this explains why. A reason rather
   * than a boolean, so the item can never be disabled without one — and so the
   * wording stays with the caller that knows the domain; this menu has no
   * opinion about why an action might be unavailable.
   *
   * Inert rather than hidden: an action that silently vanishes reads as a bug,
   * and the reason belongs where the user reaches for it.
   */
  deleteDisabledReason?: string;
  onViewDetails: () => void;
  onReorder: () => void;
  onChangeSeedTarget?: () => void;
  onUpdateDefaults?: () => void;
  /** Task rows only. */
  onDuplicate?: () => void;
  onDelete: () => void;
}

/** Ties the disabled Delete to its explanation for assistive technology. */
const DELETE_DISABLED_REASON_ID = 'row-menu-delete-disabled-reason';

/** Breathing room kept between the menu and the edge of the viewport. */
const VIEWPORT_MARGIN_PX = 8;

/**
 * Row actions menu for the input tasks grid, shared by the right-click handler
 * and the hover three-dot button below — both drive the same `contextMenuState`
 * in TasksTableGrid, so the two triggers always open an identical menu.
 *
 * Must be rendered at the grid's root, never inside a row: it is positioned
 * `fixed`, and the step sub-table carries `transform: translateZ(0)`, which
 * would otherwise become its containing block and place it in the wrong spot.
 *
 * Sits above the right panel's edge strip, which also claims `z-50` and would
 * otherwise paint over a menu opened near the right-hand edge of the grid.
 */
const TaskRowContextMenu = React.forwardRef<
  HTMLDivElement,
  TaskRowContextMenuProps
>(
  (
    {
      x,
      y,
      alignRight,
      showViewDetails,
      showReorder,
      showChangeSeedTarget,
      showUpdateDefaults,
      deleteDisabledReason,
      onViewDetails,
      onReorder,
      onChangeSeedTarget,
      onUpdateDefaults,
      onDuplicate,
      onDelete,
    },
    ref
  ) => {
    const innerRef = useRef<HTMLDivElement | null>(null);
    const [position, setPosition] = useState({ left: x, top: y });

    // Both the size of the menu and the room around it are only knowable once it
    // is in the DOM, so resolve the final spot here: hang it leftward from a
    // right-aligned anchor, then pull it back inside the viewport if either edge
    // would otherwise overflow. `useLayoutEffect` runs before paint, so the
    // unresolved position is never visible.
    useLayoutEffect(() => {
      const element = innerRef.current;
      if (!element) return;
      const { width, height } = element.getBoundingClientRect();
      const anchorLeft = alignRight ? x - width : x;
      const left = Math.max(
        VIEWPORT_MARGIN_PX,
        Math.min(anchorLeft, window.innerWidth - width - VIEWPORT_MARGIN_PX)
      );
      const top = Math.max(
        VIEWPORT_MARGIN_PX,
        Math.min(y, window.innerHeight - height - VIEWPORT_MARGIN_PX)
      );
      setPosition((current) =>
        current.left === left && current.top === top ? current : { left, top }
      );
    }, [
      x,
      y,
      alignRight,
      showViewDetails,
      showChangeSeedTarget,
      showUpdateDefaults,
      deleteDisabledReason,
      onDuplicate,
    ]);

    // Keeps the measuring ref while still handing the node to the grid, which
    // needs it for its outside-click listener.
    const assignRef = useCallback(
      (node: HTMLDivElement | null) => {
        innerRef.current = node;
        if (typeof ref === 'function') {
          ref(node);
        } else if (ref) {
          ref.current = node;
        }
      },
      [ref]
    );

    return (
      <div
        ref={assignRef}
        // w-max so the clamp never measures a wrapped, too-narrow menu near the edge
        className="row-actions-menu fixed z-[60] w-max rounded-md border border-gray-bright bg-white shadow-lg"
        // Runtime coordinates, consumed by .row-actions-menu in App.css
        style={
          {
            '--row-menu-left': `${pxToRem(position.left)}rem`,
            '--row-menu-top': `${pxToRem(position.top)}rem`,
          } as React.CSSProperties
        }
      >
        {/* Items are text-base (14px), the grid's own size — they used to
            inherit the larger page default (OP-2481). */}
        {showViewDetails && (
          <button
            type="button"
            className="flex w-full items-center gap-1 px-3 py-2 text-left text-base text-gray-dark hover:bg-rowHover"
            onClick={(event) => {
              event.preventDefault();
              event.stopPropagation();
              onViewDetails();
            }}
          >
            <BiInfoCircle className="text-base text-secondary-dark" />
            <span>View Details</span>
          </button>
        )}
        {showReorder && (
          <button
            type="button"
            className="flex w-full items-center gap-1 px-3 py-2 text-left text-base text-gray-dark hover:bg-rowHover"
            onClick={(event) => {
              event.preventDefault();
              event.stopPropagation();
              onReorder();
            }}
          >
            <MdDragIndicator className="text-base text-secondary-dark" />
            <span>Reorder</span>
          </button>
        )}
        {showChangeSeedTarget && onChangeSeedTarget && (
          <button
            type="button"
            className="flex w-full items-center gap-1 px-3 py-2 text-left text-base text-charcoal hover:bg-rowHover"
            onClick={(event) => {
              event.preventDefault();
              event.stopPropagation();
              onChangeSeedTarget();
            }}
          >
            <BiLink className="text-base text-secondary-dark" />
            <span>{CHANGE_SEED_TARGET_MENU_LABEL}</span>
          </button>
        )}
        {showUpdateDefaults && onUpdateDefaults && (
          <button
            type="button"
            className="flex w-full items-center gap-1 px-3 py-2 text-left text-gray-dark hover:bg-rowHover"
            onClick={(event) => {
              event.preventDefault();
              event.stopPropagation();
              onUpdateDefaults();
            }}
          >
            <BiRefresh className="text-lg text-secondary-dark" />
            <span>{UPDATE_DEFAULTS_MENU_LABEL}</span>
          </button>
        )}
        {onDuplicate && (
          <button
            type="button"
            className="flex w-full items-center gap-1 px-3 py-2 text-left text-base text-gray-dark hover:bg-rowHover"
            onClick={(event) => {
              event.preventDefault();
              event.stopPropagation();
              onDuplicate();
            }}
          >
            <BiCopy className="text-base text-secondary-dark" />
            <span>{DUPLICATE_TASK_MENU_LABEL}</span>
          </button>
        )}
        <button
          type="button"
          disabled={!!deleteDisabledReason}
          title={deleteDisabledReason}
          aria-describedby={
            deleteDisabledReason ? DELETE_DISABLED_REASON_ID : undefined
          }
          className={MergeClassNames(
            'flex w-full items-center gap-1 px-3 py-2 text-left text-base text-gray-dark',
            deleteDisabledReason
              ? 'cursor-not-allowed opacity-50'
              : 'hover:bg-rowHover'
          )}
          onClick={(event) => {
            event.preventDefault();
            event.stopPropagation();
            // Unreachable: React does not dispatch click to a disabled
            // button. Kept so the guard survives the attribute being lost.
            if (deleteDisabledReason) return;
            onDelete();
          }}
        >
          <BiTrash className="text-base text-secondary-dark" />
          <span>Delete</span>
        </button>
        {deleteDisabledReason && (
          <p
            id={DELETE_DISABLED_REASON_ID}
            className="max-w-[16rem] px-3 pb-2 text-xs text-danger-dark"
          >
            {deleteDisabledReason}
          </p>
        )}
      </div>
    );
  }
);

TaskRowContextMenu.displayName = 'TaskRowContextMenu';

interface RowKebabButtonProps {
  label: string;
  /** True while this row's menu is the one currently open. */
  isActive: boolean;
  onOpen: (event: React.MouseEvent<HTMLButtonElement>) => void;
  /** Light dots, for a dark ground (the sheet's Task column in Actual). */
  isOnDark?: boolean;
}

/**
 * Three-dot control revealed when its row is hovered or holds focus, relying
 * on a `group` class on the owning row.
 *
 * `tabIndex={-1}` keeps it out of the Tab sequence that walks the grid's
 * EditableCells (OP-2276), so it is pointer-driven; keyboard users reach the
 * same menu with the context-menu key on a focused cell, which bubbles to the
 * row's own handler. `group-focus-within` at least surfaces the affordance
 * once a cell in the row is focused.
 *
 * It is `pointer-events-none` while invisible, so on a device without hover a
 * button that never appears cannot swallow taps at the row's edge.
 *
 * Stopping `onMouseDown` is required: the menu's outside-click listener is a
 * non-capturing document `mousedown`, which would otherwise close the menu a
 * moment before the click could toggle it shut. (rc-trigger registers its own
 * listeners with `capture: true`, so the antd popovers still close normally.)
 */
export const RowKebabButton: React.FC<RowKebabButtonProps> = ({
  label,
  isActive,
  onOpen,
  isOnDark = false,
}) => (
  <Tooltip title="More options">
    <button
      type="button"
      aria-label={label}
      aria-haspopup="menu"
      aria-expanded={isActive}
      tabIndex={-1}
      className={MergeClassNames(
        'flex flex-shrink-0 cursor-pointer flex-col items-center justify-center gap-thin rounded-md p-xs transition hover:bg-gray-argent',
        ROW_CONTROL_REVEAL_CLASSES,
        // Without this the trigger fades out as soon as the pointer leaves the
        // row for the menu it just opened, taking the click-again-to-close
        // affordance with it.
        isActive && ROW_CONTROL_ACTIVE_CLASSES
      )}
      onMouseDown={(event) => event.stopPropagation()}
      onClick={onOpen}
    >
      {/* Three separately-spaced dots per design reference, not the tight
          IoEllipsisVertical glyph it replaced — spans, not an icon font, so
          the spacing between them is controlled directly (gap-thin) rather
          than baked into a single glyph's own metrics. Dot size, gap and
          button padding are all half the original h-1.5/gap-1/p-1.5 (using
          the closest tokens — --spacing-xs and --spacing-thin are each
          exactly half of the original px values) per feedback that the
          whole control read as too large. */}
      {[0, 1, 2].map((dot) => (
        <span
          key={dot}
          className={MergeClassNames(
            'h-xs w-xs rounded-full',
            isOnDark ? 'bg-gray-bright' : 'bg-secondary-dark'
          )}
        />
      ))}
    </button>
  </Tooltip>
);

interface RowGripButtonProps {
  label: string;
  /** True while this row is the one currently in reorder mode. */
  isActive: boolean;
  onActivate: (event: React.MouseEvent<HTMLButtonElement>) => void;
  /**
   * Lets the owning row carry `draggable` for as long as the pointer is over
   * this handle. HTML5 drag and drop has no notion of a drag handle — only
   * whole elements are `draggable` — so the row has to be told when the pointer
   * is somewhere a drag is allowed to begin.
   */
  onArmDrag: (armed: boolean) => void;
  /**
   * Reports that a press began on this handle. Since `dragstart` bubbles, the
   * row cannot tell a drag of itself from a text selection dragged out of one
   * of its cells; where the press started is what separates them.
   */
  onPressStart: () => void;
}

/**
 * Grip handle on an input-step row, driving both re-order gestures: clicking
 * it puts that row into reorder mode, after which ↑/↓ move the step one
 * position at a time, and dragging from it moves the step by pointer.
 *
 * Shares the kebab's reveal contract and its `tabIndex={-1}` reasoning. Note
 * that focus for reorder mode is deliberately hosted on the steps-table
 * wrapper rather than here: a step moving *down* re-inserts its own `<tr>` in
 * the DOM, which would blur anything inside it and drop the user out of the
 * mode after a single keypress.
 */
export const RowGripButton: React.FC<RowGripButtonProps> = ({
  label,
  isActive,
  onActivate,
  onArmDrag,
  onPressStart,
}) => (
  <Tooltip title="Re-order this input">
    <button
      type="button"
      aria-label={label}
      aria-pressed={isActive}
      {...{ [STEP_REORDER_CONTROL_ATTR]: '' }}
      tabIndex={-1}
      className={MergeClassNames(
        'flex flex-shrink-0 items-center rounded-md text-base text-secondary-dark transition hover:bg-gray-argent',
        isActive ? 'cursor-grabbing' : 'cursor-grab',
        ROW_CONTROL_REVEAL_CLASSES,
        isActive && ROW_CONTROL_ACTIVE_CLASSES
      )}
      // Armed on hover rather than on mousedown: WebKit resolves the drag
      // source as the button goes down, so a `draggable` attribute added during
      // mousedown arrives too late and Safari starts no drag at all. Hovering
      // is just as narrow a gate — the pointer has to be on this handle, which
      // is the whole requirement — and it cannot strand a row still draggable
      // after a press that ended somewhere else.
      onMouseEnter={() => onArmDrag(true)}
      onMouseLeave={() => onArmDrag(false)}
      onMouseDown={(event) => {
        event.stopPropagation();
        // Only a primary-button press can begin a drag.
        if (event.button === 0) onPressStart();
      }}
      onClick={onActivate}
    >
      <MdDragIndicator />
    </button>
  </Tooltip>
);

export default TaskRowContextMenu;
